/**
 * Gallery Sync Service
 * Handles publish/list/get operations for the P5 AI Gallery
 * Similar pattern to p5sync.js
 */

import { useStore } from '../store/useStore'

/**
 * Publish current sketch to the gallery
 * @param {Object} options - Publish options
 * @param {string} options.title - Sketch title
 * @param {string} options.slug - URL-safe slug
 * @param {string} options.description - Optional description
 * @param {string} options.tags - Optional tags (comma/space separated)
 * @param {string} options.thumbnail - Optional base64 thumbnail image
 * @param {string} options.publishAs - 'public' or 'user'
 * @param {Array} options.aiChat - Optional AI chat messages to include
 * @returns {Promise<Object>} - Publish result with URLs
 */
export async function publishToGallery(options) {
  const { files, sketchName, p5Version, p5Credentials } = useStore.getState()
  const { title, slug, description, tags, thumbnail, publishAs, aiChat } = options
  
  // Determine username (null for public, 'username-p5js' for user gallery)
  const username = publishAs === 'user' && p5Credentials?.username
    ? `${p5Credentials.username}-p5js`
    : null
  
  const response = await fetch('/api/gallery-publish', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      title: title || sketchName,
      slug,
      description: description || '',
      tags: tags || '',
      thumbnail: thumbnail || null,
      aiChat: aiChat || null,
      files: files.map(f => ({
        name: f.name,
        content: f.content
      })),
      username,
      p5Version
    })
  })
  
  const data = await response.json()
  
  if (!response.ok) {
    throw new Error(data.error || 'Publish failed')
  }
  
  return data
}

/**
 * List sketches from the gallery
 * @param {Object} options - List options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 20)
 * @param {string} options.username - Filter by username (optional)
 * @returns {Promise<Object>} - List result with sketches and pagination
 */
export async function listGallerySketches(options = {}) {
  const { page = 1, limit = 20, username } = options
  
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString()
  })
  
  if (username) {
    params.set('user', username)
  }
  
  const response = await fetch(`/api/gallery-list?${params}`)
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to list sketches')
  }
  
  return response.json()
}

/**
 * Get a single sketch from the gallery
 * @param {string} slug - Sketch slug
 * @param {string} username - Username (optional, null for anonymous)
 * @param {boolean} increment - Increment view count (default: false)
 * @returns {Promise<Object>} - Sketch data
 */
export async function getGallerySketch(slug, username = null, increment = false) {
  const params = new URLSearchParams({ slug })
  
  if (username) {
    params.set('user', username)
  }
  
  if (increment) {
    params.set('increment', 'true')
  }
  
  const response = await fetch(`/api/gallery-get?${params}`)
  
  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || 'Failed to get sketch')
  }
  
  return response.json()
}

/**
 * Generate URL-safe slug from text
 * @param {string} text - Input text
 * @returns {string} - URL-safe slug
 */
export function slugify(text) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60)
}

/**
 * Get gallery URL for a sketch
 * @param {string} slug - Sketch slug
 * @param {string} username - Username (optional)
 * @returns {string} - Full gallery URL
 */
export function getGalleryUrl(slug, username = null) {
  if (username) {
    return `https://p5js.ai/gallery/u/${username}/${slug}`
  }
  return `https://p5js.ai/gallery/s/${slug}`
}
